import { runSingleCompanyEnrichment } from "../../../container/Enrichment/runSingleCompanyEnrichment";
import { isCompanyActionEligible } from "../../../helper/companyEligibility";
import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Button } from "@mui/material";
import SpreadsheetActionsMenu from "../../../container/Enrichment/SpreadsheetActionsMenu";
import { DEFAULT_SPREADSHEET_MODEL_ID } from "../../../helper/aiEnrichment";
import { normalizeEmails, parseApiError } from "../../../helper/spreadsheetView";
import { CompanyPropTypes } from "../../../schema/company";
import { getEnrichmentPromptLabel } from "./companyEnrichmentFormat";
import { createCombinedEmailRunner } from '../../../container/Enrichment/combinedEmailLoader';

function createHistoryEntryId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Compact primary Actions CTA for Company Details.
 * Reuses SpreadsheetActionsMenu; runs actions for the open company only.
 */
const CompanyDetailsActions = forwardRef(function CompanyDetailsActions(
  {
    companyDetail,
    draftSessionId,
    onDraftSessionUsed,
    onRunEnrichmentAction,
    onActionStart,
    onActionResult,
  },
  ref
) {
  const buttonRef = useRef(null);
  const actionInFlight = useRef(false);
  const combinedEmailRunner = useRef(null);
  if (!combinedEmailRunner.current) combinedEmailRunner.current = createCombinedEmailRunner();
  const [pending, setPending] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [view, setView] = useState("root");
  const [dataEnrichmentSelection, setDataEnrichmentSelection] = useState(null);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiModelId, setAiModelId] = useState(DEFAULT_SPREADSHEET_MODEL_ID);

  const open = Boolean(anchorEl);

  const closeMenu = () => {
    setAnchorEl(null);
    setView("root");
    setDataEnrichmentSelection(null);
  };

  const openMenu = () => {
    if (buttonRef.current && isCompanyActionEligible(companyDetail) && !actionInFlight.current) {
      setAnchorEl(buttonRef.current);
      setView("root");
      setDataEnrichmentSelection(null);
    }
  };

  useImperativeHandle(ref, () => ({
    openMenu,
  }));

  const handleOpen = (event) => {
    if (!isCompanyActionEligible(companyDetail) || actionInFlight.current) return;
    setAnchorEl(event.currentTarget);
    setView("root");
    setDataEnrichmentSelection(null);
  };

  const runAction = async (actionType, options = {}) => {
    if (!isCompanyActionEligible(companyDetail) || actionInFlight.current) return;
    actionInFlight.current = true;
    setPending(true);
    const entryId = createHistoryEntryId();
    try {
      closeMenu();
      const prompt = getEnrichmentPromptLabel(actionType, options);

      onActionStart?.({
        id: entryId,
        actionType,
        prompt,
        options,
      });

      const reportStatus = (statusPayload) => {
        onActionResult?.({ ...statusPayload, entryId });
      };

      if (typeof onRunEnrichmentAction === "function") {
        await onRunEnrichmentAction(actionType, {
          ...options,
          companyIds: [companyDetail?.stagedId],
          onCompanyStatus: (entry) => {
            reportStatus({ actionType, ...entry });
          },
        });
        return;
      }

      await runSingleCompanyEnrichment({
        actionType,
        company: companyDetail,
        options: { ...options, combinedEmailRunner: combinedEmailRunner.current },
        draftSessionId,
        onDraftSessionUsed,
        onStatus: reportStatus,
      });
    } catch (error) {
      const { msg } = parseApiError(error, "Enrichment failed");
      onActionResult?.({ entryId, actionType, status: "failed", error: msg });
    } finally {
      actionInFlight.current = false;
      setPending(false);
    }
  };

  return (
    <>
      <Button
        ref={buttonRef}
        variant="contained"
        disableElevation
        size="small"
        data-testid="company-details-actions"
        onClick={handleOpen}
        disabled={!isCompanyActionEligible(companyDetail) || pending}
        sx={{
          textTransform: "none",
          fontWeight: 700,
          fontSize: 12.5,
          lineHeight: 1.2,
          minHeight: 32,
          px: 1.5,
          borderRadius: "8px",
          flexShrink: 0,
        }}
      >
        Actions
      </Button>
      <SpreadsheetActionsMenu
        open={open}
        anchorEl={anchorEl}
        onClose={closeMenu}
        view={view}
        onViewChange={setView}
        selectedCount={1}
        verifyEmailCount={normalizeEmails(companyDetail?.emails).length}
        appliesLabelOverride="Applies to 1 company"
        dataEnrichmentSelection={dataEnrichmentSelection}
        onDataEnrichmentSelectionChange={setDataEnrichmentSelection}
        aiPrompt={aiPrompt}
        onAiPromptChange={setAiPrompt}
        aiModelId={aiModelId}
        onAiModelIdChange={setAiModelId}
        onContinueEnrichment={(actionId) => runAction(actionId)}
        onGenerateAi={() =>
          runAction("ai_enrichment", { query: aiPrompt, model: aiModelId })
        }
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      />
    </>
  );
});

CompanyDetailsActions.propTypes = {
  companyDetail: CompanyPropTypes,
  draftSessionId: PropTypes.string,
  onDraftSessionUsed: PropTypes.func,
  onRunEnrichmentAction: PropTypes.func,
  onActionStart: PropTypes.func,
  onActionResult: PropTypes.func,
};

export default CompanyDetailsActions;
